import debounce from 'lodash/debounce';
import {provide, inject, type MaybeRef, unref} from 'vue';

type MatomoInstance = {
    push: (action: MatomoAction) => void;
};

type MatomoAction = [string, ...MatomoValue[]];
type MatomoValue = string | number | undefined | null;

type CustomWindow = {
    _paq?: unknown;
} & Window;

declare const window: CustomWindow;

const debounceTime = 200;

/**
 * Tracks a view for the current page debounced.
 *
 * The debounced function is using a relatively small debounce time to prevent
 * view tracking of automatic page changes (automatic redirects, component navigation guards etc.)
 */
export const trackMatomoPageView = debounce(() => {
    const _paq = getMatomoInstance();
    if (!_paq) {
        return;
    }
    _paq.push(['setCustomUrl', window.location.href]);
    _paq.push(['setDocumentTitle', document.title]);
    _paq.push(['trackPageView']);
}, debounceTime);

export function trackMatomoEvent(
    category: string,
    action: string,
    name?: string,
    value?: string | number
) {
    const _paq = getMatomoInstance();
    if (!_paq) {
        return;
    }

    const trackArray: MatomoAction = ['trackEvent', category, action];
    if (name || value) {
        trackArray.push(name);
    }
    if (value) {
        trackArray.push(value);
    }
    _paq.push(trackArray);
}

/**
 * Returns an eventTracker object that allows tracking of events of a certain category.
 * If a category is passed, this category will be used and also provided to child components using `provide`.
 * If no category is passed, it will inject an event tracker that was provided by a parent, if found -
 * otherwise a no-op stub is used.
 */
export function useMatomoEventTracker(category?: MaybeRef<string>) {
    const key = 'eventTracker';

    if (category) {
        const eventTracker = {
            trackEvent(action: string, name?: string, value?: string | number) {
                trackMatomoEvent(unref(category), action, name, value);
            },
        };
        provide(key, eventTracker);

        return eventTracker;
    } else {
        return inject(key, {trackEvent() {}});
    }
}

export function setMatomoUserId(userId?: string | null) {
    const _paq = getMatomoInstance();
    if (!_paq) {
        return;
    }

    _paq.push(['setUserId', userId]);
}

function getMatomoInstance(): MatomoInstance | null {
    const _paq = window._paq;
    if (_paq && typeof _paq === 'object' && 'push' in _paq && typeof _paq.push === 'function') {
        return _paq as MatomoInstance;
    }

    return null;
}

export const booking2TrackingIndicator = 'Booking 2.0';
